import { ref } from 'vue'
import { useMenusPublicService } from '~/services/api/menus/public/useMenusPublicService'
import type {
  CartItemView,
  MenuItemPriceType,
  OrderModificationRequestLineItem
} from '~/services/api/generated'
import type { OrderItem } from '~/types/order/OrderItem'
import { MenuItemPriceType as MenuItemPriceTypeValue } from '~/enums'
import { useLogger } from '~/composables/useLogger'
import { useMenuItemPriceTypeCopy } from '~/composables/useMenuItemPriceTypeCopy'

export const useStoreMenuItemPriceTypes = () => {
  const logger = useLogger('useStoreMenuItemPriceTypes')
  const priceTypeCopy = useMenuItemPriceTypeCopy()
  const priceTypesByItemId = ref<Record<number, MenuItemPriceType>>({})

  const resolvePriceType = (
    item: CartItemView | OrderItem | OrderModificationRequestLineItem
  ): MenuItemPriceType | undefined => {
    if ('menu_item_id' in item) {
      return priceTypesByItemId.value[item.menu_item_id]
    }

    if (
      'menu_item' in item &&
      item.menu_item !== null &&
      item.menu_item !== undefined &&
      'id' in item.menu_item
    ) {
      return priceTypesByItemId.value[item.menu_item.id]
    }

    return undefined
  }

  const loadPriceTypes = async (storeSlug: string) => {
    if (storeSlug.length === 0) return

    try {
      const response = await useMenusPublicService().getStoreMenusBySlug({ slug: storeSlug })
      const nextPriceTypes: Record<number, MenuItemPriceType> = {}

      for (const menu of response.menus) {
        for (const category of menu.categories) {
          for (const item of category.menu_items) {
            nextPriceTypes[item.id] = item.price_type
          }
        }
      }

      priceTypesByItemId.value = nextPriceTypes
    } catch (error) {
      logger.error('loadPriceTypes', error)
    }
  }

  const getQuantityLabel = (item: CartItemView | OrderItem | OrderModificationRequestLineItem) => {
    return resolvePriceType(item) === 'per_person' ? 'Guests' : 'Qty'
  }

  const getUnitPriceLabel = (item: CartItemView | OrderItem | OrderModificationRequestLineItem) => {
    return priceTypeCopy.unitLabel(resolvePriceType(item) ?? MenuItemPriceTypeValue.EACH)
  }

  return {
    priceTypesByItemId,
    loadPriceTypes,
    getQuantityLabel,
    getUnitPriceLabel
  }
}
